import eyeSvg from '@/assets/icons-normalized/eye.svg?raw';
import { Table, type TableColumn } from '@shelflix/ui-admin';
import { Card } from '@shelflix/ui-web/components/ui/card';
import AppIcon from '@shelflix/ui-web/components/AppIcon';
import { MaterialDto } from '@shelflix/trpc/routes/material/list/useCase';
import { useEffect } from 'react';
import clockZzSvg from '@/assets/icons-normalized/clock-zz.svg?raw';
import offlineSvg from '@/assets/icons-normalized/offline.svg?raw';
import wifiWarningSvg from '@/assets/icons-normalized/wifi-warning.svg?raw';
import { useNavigate } from 'react-router-dom';

export interface MaterialTableProps {
  data: MaterialDto[];
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize?: number;
}

export function MaterialTable({ data, currentPage, setCurrentPage, pageSize = 10 }: MaterialTableProps) {
  const navigate = useNavigate();
  const totalPages = Math.ceil((data.length || 0) / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = (data || []).slice(startIndex, endIndex);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const statusTag = (status: string) => {
    switch (status) {
      case 'OPERATIONALONLINE':
        status = 'ACTIVE';
        break;
      case 'STANDBY':
        status = 'MAINTENANCE';
        break;
      default:
        break;
    }

    switch (status) {
      case 'ACTIVE':
        return (
          <div
            className="mx-2.5 flex items-center justify-center gap-0.5 rounded-[5px] border px-4 py-1.5 text-[10px]"
            style={{
              backgroundColor: `var(--status-${status.toLowerCase()}-bg)`,
              borderColor: `var(--status-${status.toLowerCase()})`,
              color: `var(--status-${status.toLowerCase()})`,
            }}
          >
            <AppIcon
              source="svg"
              name="online"
              svgContent={wifiWarningSvg}
              size={18}
              weight={400}
              colorStyle={{ type: 'solid', color: `var(--status-${status.toLowerCase()})` }}
            />
            <span>Online</span>
          </div>
        );
      case 'OFFLINE':
        return (
          <div
            className="mx-2.5 flex items-center justify-center gap-0.5 rounded-[5px] border px-4 py-1.5 text-[10px]"
            style={{
              backgroundColor: `var(--status-${status.toLowerCase()}-bg)`,
              borderColor: `var(--status-${status.toLowerCase()})`,
              color: `var(--status-${status.toLowerCase()})`,
            }}
          >
            <AppIcon
              source="svg"
              name="online"
              svgContent={offlineSvg}
              size={18}
              weight={400}
              colorStyle={{ type: 'solid', color: `var(--status-${status.toLowerCase()})` }}
            />
            <span>Offline</span>
          </div>
        );
      case 'MAINTENANCE':
        return (
          <div
            className="mx-2.5 flex items-center justify-center gap-0.5 rounded-[5px] border px-4 py-1.5 text-[10px]"
            style={{
              backgroundColor: `var(--status-${status.toLowerCase()}-bg)`,
              borderColor: `var(--status-${status.toLowerCase()})`,
              color: `var(--status-${status.toLowerCase()})`,
            }}
          >
            <AppIcon
              source="svg"
              name="online"
              svgContent={clockZzSvg}
              size={18}
              weight={400}
              colorStyle={{ type: 'solid', color: `var(--status-${status.toLowerCase()})` }}
            />
            <span>Manutenção</span>
          </div>
        );
      default:
        return <span>{status}</span>;
    }
  };

  const columns: TableColumn<MaterialDto>[] = [
    {
      key: 'code',
      header: 'Num de Série',
      headerTextColor: '#B2A598',
      render: (row) => <span className="truncate text-xs text-[#F2E6DA]">{row.code}</span>,
      width: '12%',
      fontSize: '14px',
    },
    {
      key: 'name',
      header: 'Material',
      headerTextColor: '#B2A598',
      render: (row) => <span className="truncate text-xs text-[#F2E6DA]">{row.name}</span>,
      width: '15%',
      fontSize: '14px',
    },
    {
      key: 'currentCampaign.name',
      header: 'Campanha',
      headerTextColor: '#B2A598',
      render: (row) => <span className="truncate text-xs text-[#F2E6DA]">{row.currentCampaign?.name}</span>,
      width: '20%',
      fontSize: '14px',
    },
    {
      key: 'facility.address',
      header: 'Endereço',
      headerTextColor: '#B2A598',
      render: (row) => (
        <div className="flex flex-col">
          <span className="truncate text-base text-[#F2E6DA]">
            {row.facility.address}, {row.facility.number}
          </span>
          <span className="truncate text-xs text-[#F2E6DA]">
            {row.facility.stateName} - {row.facility.cityName} | {row.facility.name}
          </span>
        </div>
      ),
      width: '32%',
      fontSize: '14px',
    },
    {
      key: 'status',
      header: 'Status',
      headerTextColor: '#B2A598',
      render: (row) => statusTag(row.status),
      width: '12%',
      fontSize: '14px',
    },
    {
      key: 'actions',
      header: 'Ações',
      headerTextColor: '#B2A598',
      render: (row) => (
        <div>
          <button className="h-6 w-6 cursor-pointer" onClick={() => navigate(`${row.uuid}`)}>
            <AppIcon
              source="svg"
              name="eye"
              svgContent={eyeSvg}
              size={24}
              weight={400}
              colorStyle={{ type: 'solid', color: '#E57300' }}
            />
          </button>
        </div>
      ),
      width: '10%',
      align: 'center',
      fontSize: '14px',
    },
  ];

  return (
    <Card className="bg-[#0E0E0E]">
      <Table
        columns={columns}
        data={paginatedData}
        emptyMessage="Nenhum material encontrado"
        pagination={
          (data.length || 0) > pageSize
            ? {
                currentPage: currentPage,
                totalPages: totalPages,
                totalItems: data.length || 0,
                pageSize: pageSize,
                onPageChange: setCurrentPage,
              }
            : undefined
        }
      />
    </Card>
  );
}
